import React, { JSXElementConstructor, ReactElement } from 'react';
import { PlacementWithLogical, forwardRef } from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { ChevronUpIcon } from '@allergan-data-labs/alle-elements-icons/20/chevronUpIcon';
import { ChevronDownIcon } from '@allergan-data-labs/alle-elements-icons/20/chevronDownIcon';
/**
 * Similarly to ButtonSelect as ESLint is reporting a circular dependency error:
 *  - `Circular dependency between "button" and "action-menu" detected: button -> action-menu -> button`
 */
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ActionMenu } from '@allergan-data-labs/alle-elements-action-menu';
import {
  Button,
  ButtonCounter,
  ButtonProps,
  ButtonGroup,
  ButtonGroupProps,
  ButtonColorScheme,
  ButtonSizes,
  ButtonVariants,
  IconButton,
} from '../';
import {
  getSplitButtonStyles,
  getSplitButtonActionMenuStyles,
} from './split-button.theme';

export type SplitButtonMenuType = 'parallel' | 'multiSelect';
export interface SplitButtonProps<T = unknown>
  extends Omit<ButtonGroupProps, 'isAttached' | 'spacing'> {
  /**
   * The color mode of `SplitButton`.
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * The visual color appearance of `SplitButton`
   * @default action
   */
  colorScheme?: ButtonColorScheme;
  /**
   * If `true`, all wrapped button will be disabled
   *
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Props for the main button.
   */
  buttonProps?: Omit<ButtonProps, 'onClick' | 'leftIcon' | 'rightIcon'>;
  /**
   * Icon to be displayed on the left side of the main button.
   */
  leftIcon?: ReactElement<T, string | JSXElementConstructor<T>>;
  /**
   * Props for the secondary button.
   */
  iconButtonProps?: ButtonProps;
  /**
   * Dropdown menu content wrapped in ActionMenuContent, supporting multiselect options and action item lists.
   */
  menuOptions?: React.ReactNode;
  /**
   * The menu placement relative to iconButton.
   * @default bottom
   */
  placement?: PlacementWithLogical;
  /**
   * Callback fired when the main button is clicked.
   */
  onClick?: (event: React.MouseEvent) => void;
  /**
   * Callback fired when the menu is opened.
   */
  onOpen?: () => void;
  /**
   * Callback fired when the menu is closed.
   */
  onClose?: () => void;
  /**
   * Text of the main button.
   */
  label?: string;
  /**
   * The size of `SplitButton`.
   * @default sm
   */
  size?: ButtonSizes;
  /**
   * The visual style of `SplitButton`.
   * @default solid
   */
  variant?: ButtonVariants;
  /**
   * The type of menu to display. If `multi-select`, the menu will allow multiple selections and button will have a counter.
   * @default parallel
   */
  menuType?: SplitButtonMenuType;
  /**
   * Number of selected items displayed on the button. This property is only
   * effective when prop `menuType` is set to 'multiSelect'.
   */
  count?: number;
}

/**
 * `SplitButton` lets users perform an action or choose from a small group of similar actions.
 */
export const SplitButton = forwardRef<SplitButtonProps, 'div'>(
  (
    {
      label,
      buttonProps,
      iconButtonProps,
      onClick,
      onOpen,
      onClose,
      menuOptions,
      leftIcon,
      count,
      placement = 'auto-end',
      menuType = 'parallel',
      colorMode: propColorMode = 'light',
      colorScheme = 'action',
      variant = 'solid',
      size = 'md',
      ...rest
    },
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);
    const [isOpen, setIsOpen] = React.useState(false);

    const renderButton = () => {
      const isMultiSelect = menuType === 'multiSelect' && count;
      const ButtonComponent = isMultiSelect ? ButtonCounter : Button;

      const testId = isMultiSelect ? 'split-button-counter' : 'split-button';

      const commonProps = {
        'data-testid': testId,
        colorMode: colorMode,
        colorScheme: colorScheme,
        leftIcon: leftIcon,
        onClick: onClick,
        ...buttonProps,
        ...(isMultiSelect && { count: count }),
      };

      return <ButtonComponent {...commonProps}>{label}</ButtonComponent>;
    };

    const renderMenu = () => {
      const toggleChevronIcon = isOpen ? (
        <ChevronUpIcon data-testid="split-button-chevron-up-icon" />
      ) : (
        <ChevronDownIcon data-testid="split-button-chevron-down-icon" />
      );

      const closeOnSelect = menuType === 'parallel';

      const actionMenuProps = {
        onOpen: onOpenHandler,
        onClose: onCloseHandler,
        triggerElement: IconButton,
        colorMode: colorMode,
        placement: placement,
        closeOnSelect,
        triggerElementProps: {
          variant,
          colorScheme,
          colorMode,
          size,
          'aria-label': 'split-chevron-down-button',
          sx: getSplitButtonActionMenuStyles(variant),
          icon: toggleChevronIcon,
          ...iconButtonProps,
        },
      };
      return <ActionMenu {...actionMenuProps}>{menuOptions}</ActionMenu>;
    };

    const onOpenHandler = () => {
      onOpen?.();
      setIsOpen(true);
    };

    const onCloseHandler = () => {
      onClose?.();
      setIsOpen(false);
    };

    return (
      <ButtonGroup
        ref={ref}
        isAttached
        variant={variant}
        size={size}
        sx={getSplitButtonStyles({ colorMode, variant })}
        {...rest}
      >
        {renderButton()}
        {renderMenu()}
      </ButtonGroup>
    );
  }
);
